
import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { Layout } from './components/Layout';
import { Home } from './components/Home';
import { Language, Page } from './types';
import { EditorProvider, useEditor } from './contexts/EditorContext';
import { AuthProvider } from './contexts/AuthContext';
import { SEOHead } from './components/SEOHead';
import { deepMerge } from './utils/objectHelpers';
import { TRANSLATIONS } from './constants';

const ProductCatalog = lazy(() => import('./components/ProductCatalog').then(module => ({ default: module.ProductCatalog })));
const IndustryCases = lazy(() => import('./components/IndustryCases').then(module => ({ default: module.IndustryCases })));
const AboutUs = lazy(() => import('./components/AboutUs').then(module => ({ default: module.AboutUs })));
const ContactUs = lazy(() => import('./components/ContactUs').then(module => ({ default: module.ContactUs })));
const Resources = lazy(() => import('./components/Resources').then(module => ({ default: module.Resources })));
const ProductDetail = lazy(() => import('./components/ProductDetail').then(module => ({ default: module.ProductDetail })));
const BlogDetail = lazy(() => import('./components/BlogDetail').then(module => ({ default: module.BlogDetail })));

const getInitialLang = (): Language => {
  if (typeof window === 'undefined') return Language.EN;
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get('lang');
  const isValidLang = (l: string | null): l is Language => Object.values(Language).includes(l as any);
  if (isValidLang(langParam)) return langParam;
  
  const savedLang = localStorage.getItem('user-manual-lang') as Language;
  if (isValidLang(savedLang)) return savedLang;
  
  const browserLang = navigator.language.split('-')[0];
  const browserLanguageMap: Record<string, Language> = {
    'zh': Language.ZH, 'ar': Language.AR, 'fr': Language.FR, 'ru': Language.RU
  };
  if (browserLanguageMap[browserLang]) return browserLanguageMap[browserLang];
  
  return Language.EN;
};

const getInitialPage = (): Page => {
  if (typeof window === 'undefined') return 'home';
  const params = new URLSearchParams(window.location.search);
  const pageParam = params.get('page');
  const isValidPage = (p: string | null): p is Page => ['home', 'products', 'cases', 'resources', 'about', 'contact', 'product-detail', 'blog', 'selection', 'faq', 'blog-detail'].includes(p as any);
  return isValidPage(pageParam) ? pageParam : 'home';
};

const getInitialId = (pageType: 'product-detail' | 'blog-detail'): string | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const page = getInitialPage();
  if (page === pageType) return params.get('id');
  return null;
};

const getInitialInquiry = (): string => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('inquiry') ? decodeURIComponent(params.get('inquiry')!) : '';
};

const AppContent: React.FC = () => {
  const [currentLang, setLanguageState] = useState<Language>(getInitialLang);
  const [currentPage, setPageState] = useState<Page>(getInitialPage);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(() => getInitialId('product-detail'));
  const [selectedPostId, setSelectedPostId] = useState<string | null>(() => getInitialId('blog-detail'));
  const [inquiryProduct, setInquiryProduct] = useState<string>(getInitialInquiry);
  
  const { translations, seoSettings } = useEditor();

  const countryToLang: Record<string, Language> = useMemo(() => ({
    // Chinese
    'CN': Language.ZH, 'TW': Language.ZH, 'HK': Language.ZH, 'MO': Language.ZH,
    // Russian
    'RU': Language.RU, 'BY': Language.RU, 'KZ': Language.RU, 'KG': Language.RU,
    // Arabic
    'SA': Language.AR, 'AE': Language.AR, 'EG': Language.AR, 'DZ': Language.AR, 'MA': Language.AR, 'IQ': Language.AR, 'SD': Language.AR, 'YE': Language.AR, 'SY': Language.AR, 'TN': Language.AR, 'JO': Language.AR, 'LY': Language.AR, 'LB': Language.AR, 'OM': Language.AR, 'KW': Language.AR, 'QA': Language.AR, 'BH': Language.AR,
    // French
    'FR': Language.FR, 'BE': Language.FR, 'CH': Language.FR, 'CA': Language.FR, 'SN': Language.FR, 'CI': Language.FR, 'MG': Language.FR, 'CM': Language.FR, 'BF': Language.FR, 'NE': Language.FR, 'ML': Language.FR,
  }), []);
  
  // 核心改进：生成稳健的翻译字典 t
  // 优先级：当前语言字典 > 本地代码默认字典 > 英文主字典
  const t = useMemo(() => {
    const fallback = TRANSLATIONS[Language.EN];
    const localDefault = TRANSLATIONS[currentLang] || fallback;
    const remoteData = translations[currentLang] || {};
    
    // 将三者进行合并，确保 UI 文字、配置项一个都不少
    return deepMerge(deepMerge(fallback, localDefault), remoteData);
  }, [translations, currentLang]);

  // Initial Routing & History Listeners
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get('page');
      const isValidPage = (p: string | null): p is Page => ['home', 'products', 'cases', 'resources', 'about', 'contact', 'product-detail', 'blog', 'selection', 'faq', 'blog-detail'].includes(p as any);
      const page = isValidPage(pageParam) ? pageParam : 'home';
      const langParam = params.get('lang');
      const isValidLang = (l: string | null): l is Language => Object.values(Language).includes(l as any);
      
      const savedLang = localStorage.getItem('user-manual-lang') as Language;
      
      const browserLang = navigator.language.split('-')[0];
      const browserLanguageMap: Record<string, Language> = {
        'zh': Language.ZH, 'ar': Language.AR, 'fr': Language.FR, 'ru': Language.RU
      };
      
      const finalLang = isValidLang(langParam) ? langParam : 
                        (isValidLang(savedLang) ? savedLang : 
                        (browserLanguageMap[browserLang] ? browserLanguageMap[browserLang] : Language.EN));
      
      setPageState(page);
      setLanguageState(finalLang);
      
      if (page === 'product-detail') setSelectedProductId(params.get('id') || null);
      if (page === 'blog-detail') setSelectedPostId(params.get('id') || null);
      
      const inquiry = params.get('inquiry');
      if (inquiry) setInquiryProduct(decodeURIComponent(inquiry));
    };

    handlePopState();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // IP-based Language Detection (Runs ONLY Once)
  useEffect(() => {
    let isCancelled = false;

    const detectLanguage = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has('lang')) return;
      
      const savedLang = localStorage.getItem('user-manual-lang');
      if (savedLang) return; // User already has a manual preference

      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (isCancelled) return;

        // Re-check after await in case user manually changed it during the fetch
        if (localStorage.getItem('user-manual-lang') || new URLSearchParams(window.location.search).has('lang')) return;

        const countryCode = data.country_code;
        if (countryCode && countryToLang[countryCode]) {
          const detectedLang = countryToLang[countryCode];
          setLanguageState(detectedLang);
          // Only amend the URL silently
          const urlParams = new URLSearchParams(window.location.search);
          urlParams.set('lang', detectedLang);
          if (!urlParams.has('page')) urlParams.set('page', 'home');
          window.history.replaceState({}, '', `?${urlParams.toString()}`);
          return; // Successfully detected via IP
        }
      } catch (error) {
        console.warn('IP language detection skipped or failed');
      }
    };

    detectLanguage();
    return () => { isCancelled = true; };
  }, [countryToLang]);

  const updateUrl = useCallback((page: Page, lang: Language, id?: string | null, inquiry?: string) => {
    if (window.location.protocol === 'blob:' || window.location.protocol === 'data:') return;
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('lang', lang);
      if (id) params.set('id', id);
      if (inquiry) params.set('inquiry', inquiry);
      const newSearch = `?${params.toString()}`;
      if (window.location.search !== newSearch) window.history.pushState({}, '', newSearch);
    } catch (e) {
      // Ignore URL update errors
    }
  }, []);

  const setPage = (page: Page) => {
    setPageState(page);
    if (page !== 'contact') setInquiryProduct('');
    
    let idToUrl: string | null | undefined = null;
    if (page === 'product-detail') idToUrl = selectedProductId;
    if (page === 'blog-detail') idToUrl = selectedPostId;

    if (page !== 'product-detail') setSelectedProductId(null);
    if (page !== 'blog-detail') setSelectedPostId(null);

    updateUrl(page, currentLang, idToUrl);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('user-manual-lang', lang); // Save manual choice to persist over auto-detect
    const activeId = currentPage === 'product-detail' ? selectedProductId : (currentPage === 'blog-detail' ? selectedPostId : null);
    updateUrl(currentPage, lang, activeId, inquiryProduct);
  };

  const handleProductClick = (id: string) => {
    setSelectedProductId(id);
    setPageState('product-detail');
    updateUrl('product-detail', currentLang, id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBlogPostClick = (id: string) => {
    setSelectedPostId(id);
    setPageState('blog-detail');
    updateUrl('blog-detail', currentLang, id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInquire = (productName: string) => {
    setInquiryProduct(productName);
    setPageState('contact');
    updateUrl('contact', currentLang, null, productName);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home': return <Home currentLang={currentLang} t={t} setPage={setPage} onProductClick={handleProductClick} />;
      case 'products': return <ProductCatalog currentLang={currentLang} t={t} onProductClick={handleProductClick} />;
      case 'cases': return <IndustryCases currentLang={currentLang} t={t} />;
      case 'resources': return <Resources currentLang={currentLang} t={t} onBlogPostClick={handleBlogPostClick} />;
      case 'blog': return <Resources currentLang={currentLang} t={t} onBlogPostClick={handleBlogPostClick} />;
      case 'selection': return <Resources currentLang={currentLang} t={t} />;
      case 'faq': return <Resources currentLang={currentLang} t={t} />; 
      case 'about': return <AboutUs currentLang={currentLang} t={t} />;
      case 'contact': return <ContactUs currentLang={currentLang} t={t} initialProduct={inquiryProduct} />;
      case 'product-detail':
        return selectedProductId ? (
          <ProductDetail productId={selectedProductId} currentLang={currentLang} t={t} onBack={() => setPage('products')} onInquire={handleInquire} onProductClick={handleProductClick} />
        ) : (
          <ProductCatalog currentLang={currentLang} t={t} onProductClick={handleProductClick} />
        );
      case 'blog-detail':
        return selectedPostId ? (
          <BlogDetail postId={selectedPostId} currentLang={currentLang} t={t} onBack={() => setPage('blog')} />
        ) : (
          <Resources currentLang={currentLang} t={t} onBlogPostClick={handleBlogPostClick} />
        );
      default: return <Home currentLang={currentLang} t={t} setPage={setPage} onProductClick={handleProductClick} />;
    }
  };

  if (!t) return null;
  const globalTitle = seoSettings?.title?.[currentLang] || t.hero.title;
  const globalDesc = seoSettings?.description?.[currentLang] || t.hero.subtitle;

  return (
    <Layout currentLang={currentLang} setLanguage={setLanguage} currentPage={currentPage} setPage={setPage} t={t}>
      {currentPage !== 'product-detail' && currentPage !== 'blog-detail' && <SEOHead title={globalTitle} description={globalDesc} />}
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div></div>}>
        {renderPage()}
      </Suspense>
    </Layout>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <EditorProvider>
      <AppContent />
    </EditorProvider>
  </AuthProvider>
);

export default App;
